import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Sparkles, Users, BookOpen, LogOut, ChevronRight, Search,
  CheckCircle2, Circle, Loader2, Target, Eye, ChevronDown, ChevronUp
} from "lucide-react";
import { useAuth, apiCall } from "../App";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import DemoVideoButton from "../components/DemoVideo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import AdminViewBreadcrumb from "../components/AdminViewBreadcrumb";
import KoshaQuizResultsView from "../components/KoshaQuizResultsView";
import GuideViewer from "../components/GuideViewer";

const ExpertDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewAsExpertId = searchParams.get("view_as");
  const fromTab = searchParams.get("from_tab");
  const isAdminView = user?.role === "admin" && viewAsExpertId;

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [practiceUniverse, setPracticeUniverse] = useState(null);
  const [clientAssignments, setClientAssignments] = useState([]);
  const [selectedForAssignment, setSelectedForAssignment] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState({});

  const fetchDashboard = useCallback(async () => {
    const endpoint = isAdminView
      ? `/expert/dashboard?expert_id=${viewAsExpertId}`
      : "/expert/dashboard";
    const res = await apiCall("GET", endpoint, null, token);
    if (res.success) setDashboard(res.data);
    setLoading(false);
  }, [token, isAdminView, viewAsExpertId]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setSelectedForAssignment([]);
    setCollapsedCategories({});
    const [universeRes, assignRes] = await Promise.all([
      apiCall("GET", `/expert/practice-universe/${client.user_id}`, null, token),
      apiCall("GET", `/practice-assignments/client/${client.user_id}`, null, token),
    ]);
    if (universeRes.success) setPracticeUniverse(universeRes.data);
    if (assignRes.success) setClientAssignments(assignRes.data.assignments || []);
  };

  const toggleSelect = (pid) => {
    setSelectedForAssignment(prev =>
      prev.includes(pid) ? prev.filter(id => id !== pid) : [...prev, pid]
    );
  };

  const handleAssignSelected = async () => {
    if (!selectedClient || selectedForAssignment.length === 0) return;
    setAssigning(true);
    const res = await apiCall("POST", `/practice-assignments/client/${selectedClient.user_id}`, {
      practice_ids: selectedForAssignment, notes: "Assigned by Expert"
    }, token);
    if (res.success) {
      toast.success(res.data.message);
      setSelectedForAssignment([]);
      const [universeRes, assignRes] = await Promise.all([
        apiCall("GET", `/expert/practice-universe/${selectedClient.user_id}`, null, token),
        apiCall("GET", `/practice-assignments/client/${selectedClient.user_id}`, null, token),
      ]);
      if (universeRes.success) setPracticeUniverse(universeRes.data);
      if (assignRes.success) setClientAssignments(assignRes.data.assignments || []);
    } else {
      toast.error(res.error || "Assignment failed");
    }
    setAssigning(false);
  };

  const handleRemoveAssignment = async (assignmentId) => {
    const res = await apiCall("DELETE", `/practice-assignments/${assignmentId}`, null, token);
    if (res.success) {
      toast.success("Practice removed");
      setClientAssignments(prev => prev.filter(a => a.assignment_id !== assignmentId));
      // Refresh universe to update already_assigned flags
      const universeRes = await apiCall("GET", `/expert/practice-universe/${selectedClient.user_id}`, null, token);
      if (universeRes.success) setPracticeUniverse(universeRes.data);
    }
  };

  const toggleCategory = (cat) => {
    setCollapsedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cosmic-void flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-divine-gold animate-spin" />
      </div>
    );
  }

  const filteredClients = (dashboard?.clients || []).filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) || c.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-cosmic-void" data-testid="expert-dashboard">
      {/* Admin View Breadcrumb */}
      {isAdminView && (
        <AdminViewBreadcrumb
          viewerRole="admin"
          fromTab={fromTab}
          entityType="expert"
          entityName={dashboard?.name || "Expert"}
          testId="expert-view-breadcrumb"
        />
      )}
      {/* Nav */}
      <nav className={`fixed ${isAdminView ? 'top-10' : 'top-0'} w-full z-50 backdrop-blur-md border-b border-divine-gold/10 bg-cosmic-void/80`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl text-divine-gold">&#x0950;</span>
            <span className="font-heading text-xl text-divine-gold tracking-wider hidden md:inline">SIWON HARMONY</span>
            {isAdminView ? (
              <Badge className="bg-amber-500/20 text-amber-400"><Eye className="w-3 h-3 mr-1" />VIEWING EXPERT</Badge>
            ) : (
              <Badge className="bg-emerald-500/20 text-emerald-400"><Sparkles className="w-3 h-3 mr-1" />EXPERT: {user?.name}</Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            <DemoVideoButton className="text-sm" />
            <button
              onClick={() => setShowGuide(true)}
              className="p-2 hover:text-divine-gold transition-colors text-chakra-crown/70"
              title="Expert Guide"
              data-testid="open-expert-guide-btn"
            >
              <BookOpen className="w-5 h-5" />
            </button>
            {!isAdminView && (
              <button onClick={logout} className="p-2 hover:text-divine-gold transition-colors text-chakra-crown/70" data-testid="expert-logout-btn">
                <LogOut className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </nav>

      <main className={`${isAdminView ? 'pt-32' : 'pt-24'} pb-12 px-6`}>
        <div className="max-w-7xl mx-auto">
          {showGuide ? (
            <GuideViewer slug="expert" token={token} canDownload={false} onBack={() => setShowGuide(false)} />
          ) : (<>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Assigned Clients", value: dashboard?.total_clients || 0, icon: Users },
              { label: "Practice Categories", value: (dashboard?.assigned_categories || []).length, icon: BookOpen },
              { label: "Specialization", value: dashboard?.specialization || "General", icon: Sparkles },
              { label: "Total Practices", value: Object.values(dashboard?.category_practices || {}).reduce((s, v) => s + v, 0), icon: Target },
            ].map((s) => (
              <div key={s.label} className="sacred-card text-center">
                <s.icon className="w-5 h-5 text-divine-gold/50 mx-auto mb-2" />
                <p className="text-chakra-crown/50 text-xs">{s.label}</p>
                <p className="text-divine-gold font-heading text-xl mt-1">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Assigned Categories */}
          <div className="sacred-card mb-6">
            <h3 className="font-heading text-base text-divine-gold mb-3">YOUR PRACTICE CATEGORIES</h3>
            <div className="flex flex-wrap gap-2">
              {(dashboard?.assigned_categories || []).map(cat => (
                <Badge key={cat} className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-3 py-1">
                  {cat} ({dashboard?.category_practices?.[cat] || 0} practices)
                </Badge>
              ))}
              {(dashboard?.assigned_categories || []).length === 0 && (
                <p className="text-chakra-crown/40 text-sm">No categories assigned yet. Contact admin.</p>
              )}
            </div>
          </div>

          {/* Client Management */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Client List */}
            <div className="lg:col-span-1">
              <div className="sacred-card">
                <h3 className="font-heading text-base text-divine-gold mb-3">
                  <Users className="w-4 h-4 inline mr-2" />ASSIGNED CLIENTS ({dashboard?.total_clients || 0})
                </h3>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-divine-gold/40" />
                  <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown" data-testid="expert-client-search" />
                </div>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filteredClients.map(client => (
                    <button key={client.user_id} onClick={() => handleSelectClient(client)}
                      className={`w-full p-3 rounded-xl text-left transition-all ${
                        selectedClient?.user_id === client.user_id
                          ? "bg-divine-gold/20 border border-divine-gold"
                          : "bg-cosmic-light/10 border border-transparent hover:border-divine-gold/30"
                      }`} data-testid={`expert-client-${client.user_id}`}>
                      <p className="text-divine-gold font-medium text-sm">{client.name}</p>
                      <p className="text-chakra-crown/50 text-xs">{client.email}</p>
                      <div className="flex gap-2 mt-2 text-xs">
                        <span className="text-emerald-400">{client.assigned_practices || 0} practices</span>
                        <span className="text-chakra-crown/40">|</span>
                        <span className="text-sky-400">{client.completed_practices || 0} completed</span>
                      </div>
                    </button>
                  ))}
                  {filteredClients.length === 0 && (
                    <div className="text-center py-8">
                      <Users className="w-8 h-8 text-divine-gold/20 mx-auto mb-2" />
                      <p className="text-chakra-crown/40 text-sm">No clients assigned</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Client Detail */}
            <div className="lg:col-span-2">
              {selectedClient ? (
                <Tabs defaultValue="recommend">
                  <TabsList className="bg-cosmic-deep/50 border border-divine-gold/10 p-1 mb-4">
                    <TabsTrigger value="recommend" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void" data-testid="recommend-tab">
                      <Target className="w-4 h-4 mr-1" />Recommendations
                    </TabsTrigger>
                    <TabsTrigger value="assigned" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void" data-testid="assigned-tab">
                      <CheckCircle2 className="w-4 h-4 mr-1" />Assigned ({clientAssignments.length})
                    </TabsTrigger>
                    <TabsTrigger value="kosha" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void" data-testid="expert-kosha-tab">
                      <Eye className="w-4 h-4 mr-1" />Kosha Map
                    </TabsTrigger>
                  </TabsList>

                  {/* Practice Universe Tab */}
                  <TabsContent value="recommend">
                    <div className="sacred-card">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-heading text-base text-divine-gold">PRACTICE UNIVERSE</h3>
                          <p className="text-chakra-crown/50 text-sm mt-1">
                            {practiceUniverse?.total_practices || 0} practices across {practiceUniverse?.categories?.length || 0} categories for {selectedClient.name}
                          </p>
                        </div>
                        {selectedForAssignment.length > 0 && (
                          <Button onClick={handleAssignSelected} disabled={assigning} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="assign-selected-btn">
                            {assigning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                            Assign ({selectedForAssignment.length})
                          </Button>
                        )}
                      </div>

                      <div className="space-y-3 max-h-[500px] overflow-y-auto">
                        {Object.entries(practiceUniverse?.practices_by_category || {}).map(([category, practices]) => (
                          <div key={category} className="rounded-lg border border-divine-gold/15 overflow-hidden" data-testid={`universe-category-${category.replace(/\s+/g, '-').toLowerCase()}`}>
                            <button onClick={() => toggleCategory(category)}
                              className="w-full flex items-center justify-between p-3 bg-cosmic-light/10 hover:bg-cosmic-light/15 transition-colors">
                              <div className="flex items-center gap-2">
                                <BookOpen className="w-4 h-4 text-divine-gold/60" />
                                <span className="font-heading text-sm text-divine-gold">{category.toUpperCase()}</span>
                                <Badge className="bg-divine-gold/15 text-divine-gold/80 text-xs">{practices.length}</Badge>
                              </div>
                              {collapsedCategories[category] ? <ChevronDown className="w-4 h-4 text-divine-gold/50" /> : <ChevronUp className="w-4 h-4 text-divine-gold/50" />}
                            </button>
                            {!collapsedCategories[category] && (
                              <div className="p-2 space-y-1.5">
                                {practices.map(p => (
                                  <div key={p.practice_id}
                                    className={`p-2.5 rounded-lg border transition-all cursor-pointer ${
                                      selectedForAssignment.includes(p.practice_id)
                                        ? "bg-emerald-500/10 border-emerald-500/40"
                                        : p.already_assigned
                                          ? "bg-cosmic-light/5 border-divine-gold/5 opacity-60"
                                          : "bg-cosmic-light/5 border-divine-gold/10 hover:border-divine-gold/30"
                                    }`}
                                    onClick={() => !p.already_assigned && toggleSelect(p.practice_id)}
                                    data-testid={`universe-practice-${p.practice_id}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      {p.already_assigned ? (
                                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                                      ) : selectedForAssignment.includes(p.practice_id) ? (
                                        <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                                      ) : (
                                        <Circle className="w-4 h-4 text-chakra-crown/30 shrink-0" />
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <span className="text-divine-gold font-medium text-sm">{p.name}</span>
                                        <span className="text-chakra-crown/40 text-xs ml-2">{p.description}</span>
                                      </div>
                                      <div className="flex items-center gap-2 shrink-0">
                                        <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">{p.kosha_primary}</Badge>
                                        <Badge variant="outline" className={`text-xs ${
                                          p.difficulty === 'beginner' ? 'border-green-500/30 text-green-400' :
                                          p.difficulty === 'intermediate' ? 'border-amber-500/30 text-amber-400' : 'border-red-500/30 text-red-400'
                                        }`}>{p.difficulty}</Badge>
                                        <span className="text-chakra-crown/40 text-xs">{p.duration_min}m</span>
                                      </div>
                                    </div>
                                    {p.already_assigned && <span className="text-emerald-500/60 text-xs ml-7">Already assigned</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                        {(!practiceUniverse?.categories || practiceUniverse.categories.length === 0) && (
                          <p className="text-chakra-crown/40 text-center py-8">No practice categories assigned. Contact admin.</p>
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* Assigned Tab */}
                  <TabsContent value="assigned">
                    <div className="sacred-card">
                      <h3 className="font-heading text-base text-divine-gold mb-4">ASSIGNED PRACTICES ({clientAssignments.length})</h3>
                      <div className="space-y-2 max-h-[500px] overflow-y-auto">
                        {clientAssignments.map(a => (
                          <div key={a.assignment_id} className="p-3 rounded-lg bg-cosmic-light/5 border border-divine-gold/10 flex items-center justify-between" data-testid={`assignment-${a.assignment_id}`}>
                            <div className="flex items-center gap-3">
                              {a.compliance_status === "completed" ? (
                                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                              ) : (
                                <Circle className="w-5 h-5 text-chakra-crown/30" />
                              )}
                              <div>
                                <p className="text-divine-gold font-medium text-sm">{a.practice_name}</p>
                                <div className="flex gap-2 text-xs text-chakra-crown/40">
                                  <span>{a.category}</span>
                                  <span>|</span>
                                  <span>{a.kosha_primary}</span>
                                  <span>|</span>
                                  <span>by {a.assigned_by_name}</span>
                                </div>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleRemoveAssignment(a.assignment_id)}
                              className="text-chakra-crown/40 hover:text-red-400 text-xs">Remove</Button>
                          </div>
                        ))}
                        {clientAssignments.length === 0 && (
                          <p className="text-chakra-crown/40 text-center py-8">No practices assigned yet</p>
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* Kosha Tab */}
                  <TabsContent value="kosha">
                    <div className="space-y-4">
                      <div className="sacred-card" data-testid="expert-rel-kosha-quiz">
                        <KoshaQuizResultsView token={token} clientId={selectedClient.user_id} quizType="relationship" />
                      </div>
                      <div className="sacred-card" data-testid="expert-life-kosha-quiz">
                        <KoshaQuizResultsView token={token} clientId={selectedClient.user_id} quizType="life" />
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              ) : (
                <div className="sacred-card flex items-center justify-center h-96">
                  <div className="text-center">
                    <Sparkles className="w-12 h-12 text-divine-gold/20 mx-auto mb-4" />
                    <p className="text-chakra-crown/40">Select a client to view recommendations and assign practices</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          </>)}
        </div>
      </main>
    </div>
  );
};

export default ExpertDashboard;
